import { useEffect, useState } from "react";
import { card, theme } from "../theme";
import { RulerProgress } from "../components/Drawing";

// Public (unauthenticated) page where a supplier completes a security
// questionnaire via a single-use tokenised link — /supplier-questionnaire?token=…
// Mirrors the InviteRedeem pattern: token in the query string, fetch direct to
// the API, no session required.

const API = import.meta.env.VITE_API_BASE_URL as string;

interface PortalInfo {
  orgName:       string;
  supplierName:  string;
  title:         string;
  dueDate:       string | null;
  questions:     string[];
  guidance?:     Record<string, string>;   // optional help text per question, keyed by question
  answerOptions: string[];
  // Questions answered with a list of subcontractors rather than Yes / No (a subset of `questions`).
  listQuestions?: string[];
}

interface PartyRow { name: string; service: string; country: string }

const MAX_PARTIES = 100;
const EMPTY_PARTY: PartyRow = { name: "", service: "", country: "" };

function useToken(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get("token") ?? "";
}

export function SupplierQuestionnairePage() {
  const token = useToken();
  const [info,       setInfo]       = useState<PortalInfo | null>(null);
  const [fetchDone,  setFetchDone]  = useState(false);
  const [invalid,    setInvalid]    = useState(false);
  // A server or network failure is not a dead link: telling the supplier to ask for a new one
  // would void the link that still works (DOC-15).
  const [unavailable, setUnavailable] = useState(false);
  const [answers,    setAnswers]    = useState<Record<string, string>>({});
  // The rows given for each list question; an empty list is an answer (none).
  const [parties,    setParties]    = useState<Record<string, PartyRow[]>>({});
  const [name,       setName]       = useState("");
  const [email,      setEmail]      = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setInvalid(true); setFetchDone(true); return; }
    fetch(`${API}/supplier-portal/questionnaire?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (res.status === 404 || res.status === 400 || res.status === 410) { setInvalid(true); return; }
        if (!res.ok) { setUnavailable(true); return; }
        setInfo(await res.json());
      })
      .catch(() => setUnavailable(true))
      .finally(() => setFetchDone(true));
  }, [token]);

  const listSet  = new Set(info?.listQuestions ?? []);
  const isList   = (q: string) => listSet.has(q);
  // A list question is answered while every row it holds has a name (no rows means none).
  const listValid = (q: string) => (parties[q] ?? []).every((p) => p.name.trim() !== "");
  const total    = info?.questions.length ?? 0;
  const answered = info ? info.questions.filter((q) => (isList(q) ? listValid(q) : !!answers[q])).length : 0;
  const complete = total > 0 && answered === total;

  function setParty(q: string, idx: number, patch: Partial<PartyRow>) {
    setParties((all) => ({ ...all, [q]: (all[q] ?? []).map((p, i) => (i === idx ? { ...p, ...patch } : p)) }));
  }
  function addParty(q: string) {
    setParties((all) => ((all[q] ?? []).length >= MAX_PARTIES ? all : { ...all, [q]: [...(all[q] ?? []), { ...EMPTY_PARTY }] }));
  }
  function removeParty(q: string, idx: number) {
    setParties((all) => ({ ...all, [q]: (all[q] ?? []).filter((_, i) => i !== idx) }));
  }

  async function handleSubmit() {
    if (!info || !complete || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/supplier-portal/questionnaire`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          answers: info.questions.map((q) => isList(q)
            ? {
                question: q,
                answer: "",
                parties: (parties[q] ?? []).map((p) => ({
                  name: p.name.trim(),
                  ...(p.service.trim() ? { service: p.service.trim() } : {}),
                  ...(p.country.trim() ? { country: p.country.trim() } : {}),
                })),
              }
            : { question: q, answer: answers[q] }),
          respondentName:  name || undefined,
          respondentEmail: email || undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Submission failed");
      }
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Submission failed — please try again");
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "8px 12px", borderRadius: 3, fontSize: 13,
    border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.text,
    boxSizing: "border-box",
  };

  return (
    <div style={{ minHeight: "100vh", fontFamily: theme.font, background: "var(--c-bg)", padding: "40px 16px", display: "flex", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 680 }}>
        {!fetchDone && (
          <div style={{ textAlign: "center", color: theme.color.subtle, fontSize: 13, padding: "60px 0" }}>
            Loading questionnaire…
          </div>
        )}

        {fetchDone && unavailable && !invalid && (
          <div style={{ ...card, padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>
              The questionnaire could not be loaded just now
            </div>
            <div style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
              This is a temporary problem on our side, not a sign that your link has expired. Please try again in a few minutes.
            </div>
          </div>
        )}

        {fetchDone && invalid && (
          <div style={{ ...card, padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>
              Link invalid or expired
            </div>
            <div style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
              This questionnaire link is no longer valid. It may have already been submitted or expired.
              Please contact the organisation that sent it to request a new link.
            </div>
          </div>
        )}

        {fetchDone && submitted && (
          <div style={{ ...card, padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--c-successText)", marginBottom: 8 }}>
              Questionnaire submitted — thank you
            </div>
            <div style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
              Your responses have been securely returned to {info?.orgName}. You can close this page.
            </div>
          </div>
        )}

        {fetchDone && info && !submitted && (
          <div style={{ ...card, padding: "28px 32px" }}>
            <div style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
              Supplier security questionnaire
            </div>
            <div style={{ fontSize: 19, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>{info.title}</div>
            <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 4 }}>
              Sent to <strong>{info.supplierName}</strong> by <strong>{info.orgName}</strong>
              {info.dueDate && <> · due {new Date(info.dueDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</>}
            </div>
            <p style={{ fontSize: 12, color: theme.color.muted, lineHeight: 1.6, margin: "10px 0 20px" }}>
              {info.orgName} uses these answers to assess third-party security risk as part of their compliance
              programme. Answer each question below; use “Partial” where something is in progress and “N/A”
              where a question doesn't apply to your service.
              {(info.listQuestions?.length ?? 0) > 0 && " Where a question asks for your subcontractors, list them instead; an empty list means you use none."}
            </p>

            {/* Progress */}
            <RulerProgress value={answered} max={total} label="Questions answered" style={{ marginBottom: 20 }} />

            <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
              {info.questions.map((q, idx) => (
                <div key={q}>
                  <div style={{ fontSize: 13, color: theme.color.text, marginBottom: 6, lineHeight: 1.4 }}>
                    <span style={{ color: theme.color.faint, marginRight: 6 }}>{idx + 1}.</span>{q}
                  </div>
                  {info.guidance?.[q] && (
                    <div style={{ fontSize: 12, color: theme.color.muted, margin: "-2px 0 8px 20px", lineHeight: 1.5 }}>{info.guidance[q]}</div>
                  )}
                  {isList(q) ? (
                    <div style={{ marginLeft: 20 }}>
                      <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 8, lineHeight: 1.5 }}>
                        List each one: its name, what it does for you and the country it works from. Leave the list empty if you use none.
                      </div>
                      {(parties[q] ?? []).length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                          {(parties[q] ?? []).map((p, i) => (
                            <div key={i} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                              <input style={{ ...inputStyle, flex: "2 1 160px", borderColor: p.name.trim() ? "var(--c-border)" : "var(--c-warning)" }}
                                     placeholder="Name *" value={p.name} maxLength={200} aria-label={`Subcontractor ${i + 1} name`}
                                     onChange={(e) => setParty(q, i, { name: e.target.value })} />
                              <input style={{ ...inputStyle, flex: "2 1 160px" }} placeholder="Service (e.g. hosting)" value={p.service} maxLength={300}
                                     aria-label={`Subcontractor ${i + 1} service`} onChange={(e) => setParty(q, i, { service: e.target.value })} />
                              <input style={{ ...inputStyle, flex: "1 1 110px" }} placeholder="Country" value={p.country} maxLength={100}
                                     aria-label={`Subcontractor ${i + 1} country`} onChange={(e) => setParty(q, i, { country: e.target.value })} />
                              <button type="button" onClick={() => removeParty(q, i)} aria-label={`Remove subcontractor ${i + 1}`}
                                      style={{ padding: "6px 10px", borderRadius: 3, fontSize: 12, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.muted, cursor: "pointer" }}>
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <button type="button" onClick={() => addParty(q)} disabled={(parties[q] ?? []).length >= MAX_PARTIES}
                                style={{ padding: "5px 14px", borderRadius: 3, fontSize: 12, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.text, cursor: (parties[q] ?? []).length >= MAX_PARTIES ? "not-allowed" : "pointer" }}>
                          Add a subcontractor
                        </button>
                        <span style={{ fontSize: 12, color: theme.color.subtle }}>
                          {(parties[q] ?? []).length === 0 ? "None listed" : `${(parties[q] ?? []).length} listed${(parties[q] ?? []).length >= MAX_PARTIES ? ` (the most this form takes is ${MAX_PARTIES})` : ""}`}
                          {!listValid(q) ? " · give each one a name" : ""}
                        </span>
                      </div>
                    </div>
                  ) : (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {info.answerOptions.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setAnswers((a) => ({ ...a, [q]: opt }))}
                        style={{
                          padding: "5px 14px", borderRadius: 3, fontSize: 12,
                          border: answers[q] === opt ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
                          background: answers[q] === opt ? "var(--c-accentBg)" : "var(--c-panel)",
                          color: answers[q] === opt ? "var(--c-accentText)" : theme.color.muted,
                          cursor: "pointer", fontWeight: answers[q] === opt ? 600 : 400,
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ borderTop: "1px solid var(--c-border)", paddingTop: 18, marginBottom: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 10 }}>
                Completed by (optional)
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input style={{ ...inputStyle, flex: 1, minWidth: 200 }} placeholder="Your name" value={name}
                       onChange={(e) => setName(e.target.value)} maxLength={200} />
                <input style={{ ...inputStyle, flex: 1, minWidth: 200 }} placeholder="Your work email" type="email" value={email}
                       onChange={(e) => setEmail(e.target.value)} maxLength={320} />
              </div>
            </div>

            {error && (
              <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 3, background: "var(--c-dangerBg)", color: "var(--c-dangerText)", fontSize: 12 }}>
                {error}
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={!complete || submitting}
              style={{
                padding: "10px 22px", borderRadius: 3, fontSize: 14, fontWeight: 600, border: "none",
                background: complete ? theme.color.accent : "var(--c-border)",
                color: complete ? "#fff" : theme.color.muted,
                cursor: complete && !submitting ? "pointer" : "not-allowed",
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? "Submitting…" : complete ? "Submit questionnaire" : `Answer all questions to submit (${answered}/${total})`}
            </button>
            <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 10 }}>
              This link is single-use: once submitted, your answers are final and the link expires.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
